import { BookUser, Search } from "lucide-react";
import React, { useEffect, useState,useCallback } from "react";
import DataTablePagination from "../DataTablePagination";
import Spacing from "../Spacing";
import {
  getPractice,
  numberWithCommas,
  generateCCMPatientTableData,
} from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { fetchAllCCMData } from "../../services/ccm";
import debounce from "lodash.debounce";
import CCMDataTable from "../DataTable/CCMDataTable";


export default function CCMDatabase({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: 10,
    order_by: [],
  };
  const [filter, setFilter] = useState(defaultFilter);

  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [ccmPatientCount, setCcmPatientCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });


  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);

  const loadData = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchAllCCMData(filter);
      if (response) {
        const generateData = generateCCMPatientTableData(response.data?.results);
        setData(generateData);
        setCcmPatientCount({
          totalCount: response.data?.count,
          totalPages: response.data?.total_pages,
        });
        if (checkedIds.length < 1) {
          setSelectAll(false);
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadCCMPatientTableData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter((prevFilter) => ({ ...prevFilter, search_term: value, page: 1 }));
    }, 600), // 600ms debounce delay
    []
  );

  // Handle input change
  const handleChangeSearchTerm = (event) => {
    const value = event.target.value;    
    setDataLoading(true);
    debouncedSearch(value);
  };

  useEffect(() => {
    loadData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    setFilter({
      ...filter,
      account_id: account_id,
      location_id: location_id
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id,location_id]);

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };


  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);



  useEffect(() => {
    setFilter({
      ...filter,
    });
    setCheckedIds([]);
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  useDocumentTitle("CCM Notes");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_teal_bg docpro_white_color">
            <BookUser width={20} height={20} />
          </i>
          CCM Notes
          <span>{numberWithCommas(ccmPatientCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
            <div className="flex items-center">
              <form className="docpro_search_form width_100">
                <input
                  type="search"
                  placeholder="Search"
                  className="docpro_search_input"
                  onChange={handleChangeSearchTerm}
                />
                <button className="docpro_search_btn">
                  <Search width={20} height={20} />
                </button>
              </form>
            </div>
        </div>
        <CCMDataTable
          tableData={data}
          handleCheckboxChange={handleCheckboxChange}
          handleSelectAll={() => handleSelectAll(data)}
          reloadData={loadData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          globalCheckedIds={globalCheckedIds}
        />
        {data?.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={ccmPatientCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={ccmPatientCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

    </>
  );
}
